import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingOutlined, SaveOutlined } from '@ant-design/icons';
import { translate } from '@smovilgps/optimus-intl';
import { Button, Form, Modal, Steps } from 'antd-v6';
import PropTypes from 'prop-types';
import { useAppToast } from '@/components/AppToastProvider';
import { InitModelView } from './actions';
import CommandsForm from './CommandsForm';
import EventForm from './EventForm';
import ModelForm from './ModelForm';

const AddOrEditForm = ({ onCancel, onSave, setRecord, title, record, visible }) => {
	const dispatch = useDispatch();

	const brands = useSelector((state) => state.deviceModel.brands);
	const modelSendTypes = useSelector((state) => state.deviceModel.modelSendTypes);
	const events = useSelector((state) => state.modelProfile.events);
	const commandType = useSelector((state) => state.deviceModel.commandTypes);

	const intl = useIntl();
	const toast = useAppToast();
	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);
	const [eventTargetKeys, setEventTargetKeys] = useState([]);
	const [commandTargetKeys, setCommandTargetKeys] = useState([]);

	useEffect(() => {
		dispatch(InitModelView());
	}, []);

	useEffect(() => {
		if (record && visible && record.eventModel && record.commands) {
			setEventTargetKeys(record.eventModel.map((event) => event.eventId));
			setCommandTargetKeys(
				record.commands.map((command) => {
					return {
						key: command.id,
						id: command.id,
						text: command.text,
						commandTypeId: command.commandTypeId,
						isCustomerSupport: command.isCustomerSupport,
					};
				})
			);
		}
	}, [visible, record]);

	const onOk = async () => {
		setIsSaving(true);
		try {
			const values = await form.validateFields();
			const model = {
				...record,
				events: eventTargetKeys,
				commands: commandTargetKeys,
			};
			await onSave(model);
			toast.success('recordSaved', {
				record: values.description,
			});
		} catch (error) {
			setIsSaving(false);
		} finally {
			setIsSaving(false);
			setCurrentStep(0);
			onCancel();
		}
	};

	const next = async () => {
		try {
			const values = await form.validateFields();
			if (currentStep === 0) setRecord(values);
			setCurrentStep(currentStep + 1);
		} catch (error) {
			return error;
		}
	};

	const prev = () => setCurrentStep(currentStep - 1);

	const handleCommandChange = (values) => setCommandTargetKeys([...commandTargetKeys, values]);

	const removeCommand = (key) => {
		setCommandTargetKeys(commandTargetKeys.filter((command) => key !== command.key));
	};

	const onClose = () => {
		setCommandTargetKeys([]);
		setEventTargetKeys([]);
		setCurrentStep(0);

		setRecord({});
		onCancel();
	};

	const stepsContent = [
		{
			content: <ModelForm form={form} modelSendTypes={modelSendTypes} brands={brands} record={record} />,
			title: intl.formatMessage(translate('model')),
		},
		{
			content: (
				<EventForm
					form={form}
					eventTargetKeys={eventTargetKeys}
					events={events}
					handleEventChange={setEventTargetKeys}
				/>
			),
			title: intl.formatMessage(translate('events')),
		},
		{
			content: (
				<CommandsForm
					form={form}
					className="commandsSelect"
					commandTargetKeys={commandTargetKeys}
					handleCommandChange={handleCommandChange}
					commandsTypes={commandType}
					removeCommand={removeCommand}
				/>
			),
			title: intl.formatMessage(translate('commands')),
		},
	];

	const items = stepsContent.map((step) => ({ key: step.title, title: step.title }));

	const buttonGroup =
		currentStep === 0
			? [
					<Button key="next" type="primary" data-testid="btn-next-events" onClick={() => next()}>
						<FormattedMessage {...translate('next')} />
					</Button>,
			  ]
			: currentStep < 2
			? [
					<Button key="Previous" onClick={() => prev()}>
						<FormattedMessage {...translate('previous')} />
					</Button>,
					<Button key="next" type="primary" data-testid="btn-next-commands" onClick={() => next()}>
						<FormattedMessage {...translate('next')} />
					</Button>,
			  ]
			: [
					<Button key="Previous" onClick={() => prev()}>
						<FormattedMessage {...translate('previous')} />
					</Button>,
					<Button disabled={isSaving} key="save" type="primary" data-testid="btn-save" onClick={onOk}>
						{isSaving ? <LoadingOutlined /> : <SaveOutlined />}
						<FormattedMessage {...translate('save')} />
					</Button>,
			  ];

	return (
		<Modal
			open={visible}
			title={title}
			onOk={onOk}
			onCancel={onClose}
			okButtonProps={{ disabled: isSaving }}
			destroyOnHidden
			footer={buttonGroup}
			wrapClassName="data-testid-modelmodal"
		>
			<div className="manageModal">
				<Steps current={currentStep} items={items} />
				<div className="steps-content">{stepsContent[currentStep].content}</div>
			</div>
		</Modal>
	);
};

AddOrEditForm.defaultProps = {
	visible: false,
};

AddOrEditForm.propTypes = {
	title: PropTypes.string.isRequired,
	visible: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	record: PropTypes.object,
	setRecord: PropTypes.func.isRequired,
};

export default AddOrEditForm;
